import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import axios from 'helpers/api';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import CommonBreadCrumb from 'components/commonbreadcrumb/CommonBreadCrumb';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import ShimmerUI from 'components/ShimmerUI/PatientShimmer';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import paths from 'routes/paths';
import AddDayProgramPatientModal from './AddDayProgramPatientModal';

const getStoredDayPrograms = () => {
  try {
    const dayPrograms = JSON.parse(localStorage.getItem('dayPrograms') || '[]');
    return Array.isArray(dayPrograms) ? dayPrograms : [];
  } catch (error) {
    return [];
  }
};

const getDayProgramId = dayProgram =>
  dayProgram?.id || dayProgram?.day_program_id || dayProgram?.value || '';

const getDayProgramName = dayProgram =>
  dayProgram?.name ||
  dayProgram?.day_program_name ||
  dayProgram?.program_name ||
  dayProgram?.key ||
  dayProgram?.label ||
  '';

const formatDate = date =>
  date ? moment(date, ['YYYY-MM-DD', 'MM/DD/YYYY', moment.ISO_8601]).format('MM/DD/YY') : '';

const getGenderName = gender => {
  if (gender === 1 || gender === '1') return 'Male';
  if (gender === 2 || gender === '2') return 'Female';
  if (gender === 3 || gender === '3') return 'Other';
  return '';
};

const getPatientsFromResponse = data => {
  const patientCandidates = [
    data?.patients?.data,
    data?.patients,
    data?.data?.patients,
    data?.data
  ];

  return patientCandidates.find(Array.isArray) || [];
};

const ViewDayProgramPatients = () => {
  const [dayProgramOptions,setDayProgramOptions] = useState(JSON.parse(localStorage.getItem('dayProgramOptions') || '[]'));
  const navigate = useNavigate();
  const [selectedDayProgram, setSelectedDayProgram] = useState(dayProgramOptions[0]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const columns = [
    {
      accessorKey: 'patientName',
      header: 'Patient Name',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      },
      cell: ({ row: { original } }) => (
        <Link
          to={{
            pathname: paths.dayProgramPatientDetails.replace(
              ':id',
              original.patientId
            ).replace(':programId', selectedDayProgram.value)
          }}
        >
          {original.patientName}
        </Link>
      )
    },
    {
      accessorKey: 'dob',
      header: 'DOB',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      }
    },
    {
      accessorKey: 'gender',
      header: 'Gender',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      }
    },
    // {
    //   accessorKey: 'facilityName',
    //   header: 'Facility Name',
    //   meta: {
    //     headerProps: { className: 'text-900 text-start' },
    //     cellProps: { className: 'text-start' }
    //   }
    // },
    {
      accessorKey: 'enrolledDate',
      header: 'Enrolled Date',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      }
    },
    {
      accessorKey: 'startDate',
      header: 'Start Date',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      }
    },
    {
      accessorKey: 'endDate',
      header: 'End Date',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      }
    }
  ];


  const table = useAdvanceTable({
    data: patients,
    columns,
    selection: false,
    sortable: true,
    pagination: true
  });

  const fetchDayProgramPatients = async dayProgramId => {
    if (!dayProgramId) {
      setPatients([]);
      return;
    }

    setLoading(true);
    try {
      const tokenStr = localStorage.getItem('accessToken');
      const { data } = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/day-programs/patients/${dayProgramId}`,
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );
      const patientData = getPatientsFromResponse(data);
      const results = patientData
        .map(patient => {
          const firstName = patient.first_name || patient.firstName || '';
          const lastName = patient.last_name || patient.lastName || '';
          const patientName =
            patient.patient_name ||
            patient.name ||
            `${lastName}${lastName && firstName ? ', ' : ''}${firstName}`;

          return {
            patientId: patient.id || patient.patient_id || patient.patientId,
            patientName,
            dob: formatDate(patient.dob || patient.date_of_birth),
            gender: getGenderName(patient.gender),
            facilityName:
              patient.facility_name ||
              patient.facilityName ||
              patient.facility?.facility_name ||
              patient.facility?.name ||
              '',
            enrolledDate: formatDate(patient.enrolled_at),
            startDate: formatDate(patient.start_date),
            endDate: formatDate(patient.end_date)
          };
        })
        .sort((a, b) => a.patientName.localeCompare(b.patientName));

      setPatients(results);
    } catch (error) {
      setPatients([]);
      navigate('/error');
    } finally {
      setLoading(false);
    }
  };

  const handleDayProgramChange = selectedOption => {
    setSelectedDayProgram(selectedOption);
    fetchDayProgramPatients(selectedOption?.value);
  };

  useEffect(() => {
    fetchDayProgramPatients(selectedDayProgram?.value);
  }, []);

  const breadcrumbItems = [
    { name: 'Dashboard', href: '/main', isActive: false },
    { name: 'Day Program Patients', href: paths.viewDayProgramPatients, isActive: true }
  ];

  return (
    <>
      <CommonBreadCrumb breadcrumbItems={breadcrumbItems} />
      <AddDayProgramPatientModal show={showModal} onHide={() => setShowModal(false)} />
      <Card>
        <Card.Body className="p-3">
          <Row className="align-items-end mb-3">
            <Col xs="4" className="mb-3 mb-lg-0">
              <Form.Group>
                <Form.Label>Day Program</Form.Label>
                <ThemedSelect
                  name="dayProgram"
                  closeMenuOnSelect
                  placeholder="Select Day Program"
                  classNamePrefix="react-select"
                  options={dayProgramOptions}
                  value={selectedDayProgram}
                  getOptionLabel={option => option.key}
                  getOptionValue={option => option.value}
                  onChange={handleDayProgramChange}
                />
              </Form.Group>
            </Col>
            <Col xs="4" className="view-patient-search-holder">
              <AdvanceTableProvider {...table}>
                <AdvanceTableSearchBox placeholder="Search..." />
              </AdvanceTableProvider>
            </Col>
            <Col xs="4">
              <Button variant='primary' size="md" className="float-end" onClick={() => setShowModal(true)}>+ Add Patient</Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading ? (
        <ShimmerUI />
      ) : patients.length > 0 ? (
        <AdvanceTableProvider {...table}>
          <Card className="mb-3">
            <Card.Body className="p-3">
              <Row>
                <Col xs="12" className="mb-1 mt-1">
                  <AdvanceTable
                    headerClassName="bg-200 text-nowrap align-middle text-center"
                    rowClassName="text-nowrap align-middle text-center"
                    tableProps={{
                      size: 'sm',
                      striped: true,
                      className: 'fs-10 mb-0 overflow-hidden'
                    }}
                  />
                </Col>
              </Row>
            </Card.Body>
            <Card.Footer>
              <div className="mt-3">
                <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
              </div>
            </Card.Footer>
          </Card>
        </AdvanceTableProvider>
      ) : (
        <Alert variant="warning" className="text-center">
          <h5 className="mb-0">{selectedDayProgram?.value === '' ? 'Please select a Day Program' : patients.length === 0 ? 'No patients found for this Day Program' : ''}</h5>
        </Alert>
      )}
    </>
  );
};

export default ViewDayProgramPatients;